import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';

export type MosaicCardVariant =
  | 'content-light' // light-gray background, purple text — most common
  | 'content-purple' // purple background, white text — accent cards
  | 'image' // gray placeholder (design-time / missing asset)
  | 'image-photo'; // real image — cover-fit

export type MosaicCardSize = 'regular' | 'tall' | 'wide';

export interface MosaicCardProps {
  variant: MosaicCardVariant;
  size?: MosaicCardSize;
  children?: ReactNode;
  /** For `image-photo` variant — required */
  src?: string;
  /** For `image-photo` variant — required for a11y */
  alt?: string;
  className?: string;
  /** Extra classes applied to the `<img>` element (image-photo only). */
  imgClassName?: string;
  /** Animate on scroll into view (default: true). Set false to disable. */
  animate?: boolean;
  /** Seconds to delay the reveal — stagger a grid of cards */
  animationDelay?: number;
}

const variantStyles: Record<MosaicCardVariant, string> = {
  'content-light': 'bg-brand-lightgray text-brand-purple',
  'content-purple': 'bg-brand-purple text-white',
  image: 'bg-brand-darkgray',
  'image-photo': 'bg-brand-darkgray overflow-hidden',
};

const sizeStyles: Record<MosaicCardSize, string> = {
  regular: 'min-h-[320px] md:min-h-[380px]',
  tall: 'min-h-[420px] md:min-h-[560px] row-span-2',
  wide: 'min-h-[260px] md:col-span-2',
};

const contentPadding = 'p-8 md:p-10';

/**
 * Single cell of the mosaic system. MosaicGrid renders a config array of
 * these. Use `content-light` / `content-purple` for text blocks and `image`
 * / `image-photo` for visual placeholders.
 */
export function MosaicCard({
  variant,
  size = 'regular',
  children,
  src,
  alt,
  className,
  imgClassName,
  animate = true,
  animationDelay = 0,
}: MosaicCardProps) {
  const isContent = variant === 'content-light' || variant === 'content-purple';
  const isPhoto = variant === 'image-photo';

  const content = (
    <>
      {isPhoto && src && (
        <img
          src={src}
          alt={alt ?? ''}
          className={cn('absolute inset-0 h-full w-full object-cover', imgClassName)}
          loading="lazy"
          decoding="async"
        />
      )}
      {isContent && (
        <div className={cn(contentPadding, 'flex flex-col h-full')}>
          {children}
        </div>
      )}
      {variant === 'image' && children && (
        <div className={cn(contentPadding, 'flex items-center justify-center h-full text-brand-purple/40')}>
          {children}
        </div>
      )}
    </>
  );

  const shared = cn(
    'relative rounded-card-lg overflow-hidden',
    variantStyles[variant],
    sizeStyles[size],
    className
  );

  if (!animate) {
    return <div className={shared}>{content}</div>;
  }

  return (
    <motion.div
      className={shared}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: animationDelay }}
    >
      {content}
    </motion.div>
  );
}
